import React, { MouseEvent, useEffect, useState } from 'react';
import clsx from 'clsx';
import { ColumnProps, SimpleTableProps } from 'interfaces/components/tables';
import { Dropdown } from '../dropdown';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import { filterRowOptionsByRolesOrPermissions } from 'utils';
import { CypressI } from 'interfaces/cypress';
import { PaginationCustom } from '../pagination';
import { LoaderScreen } from '../loaderScreen';
import { Icon } from '../icon';
import Icons from '@/const/icons';
import { Checkbox } from '@/components/form';

const SimpleTable = <T extends { isActive?: boolean }, U = T>({
	hideRowActions = false,
	hasPagination = true,
	hasCheckboxes = false,
	isCheckboxDisabled = false,
	...props
}: SimpleTableProps<T, U> & CypressI): JSX.Element => {
	// Redefine columns to show depending if actions should be displayed or not
	const [columnsData, setColumnsData] = useState<ColumnProps<T>[]>([]);
	const [subColumnsData, setSubColumnsData] = useState<ColumnProps<U>[]>([]);
	const [expandedRows, setExpandedRows] = useState<Set<number>>(() =>
		props.defaultExpandAll && props.rows
			? new Set(props.rows.map((_, i) => i))
			: new Set()
	);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	// selection state
	const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
	const [selectedSubRows, setSelectedSubRows] = useState<
		Map<number, Set<number>>
	>(new Map());

	// helpers to read subrows given a row
	const getSubItemsOfRow = (row: T) => {
		if (props.subRowsAccessor) {
			return props.subRowsAccessor(row) as U[] | undefined;
		}
		return undefined;
	};

	// check states
	const isRowFullySelected = (rowIndex: number): boolean => {
		const subItems = getSubItemsOfRow(props.rows![rowIndex]) ?? [];
		if (!subItems || subItems.length === 0) {
			return selectedRows.has(rowIndex);
		}
		const s = selectedSubRows.get(rowIndex);
		return !!s && s.size === subItems.length;
	};

	const isRowPartiallySelected = (rowIndex: number): boolean => {
		const subItems = getSubItemsOfRow(props.rows![rowIndex]) ?? [];
		if (!subItems || subItems.length === 0) {
			return false;
		}
		const s = selectedSubRows.get(rowIndex);
		return !!s && s.size > 0 && s.size < subItems.length;
	};

	const areAllSelected = (): boolean => {
		if (!props.rows || props.rows.length === 0) return false;
		for (let i = 0; i < props.rows.length; i++) {
			const sub = getSubItemsOfRow(props.rows[i]) ?? [];
			if (!sub || sub.length === 0) {
				if (!selectedRows.has(i)) return false;
			} else {
				const s = selectedSubRows.get(i);
				if (!s || s.size !== sub.length) return false;
			}
		}
		return true;
	};

	const isSomeSelected = (): boolean => {
		if (!props.rows || props.rows.length === 0) return false;
		for (let i = 0; i < props.rows.length; i++) {
			if (selectedRows.has(i)) return true;
			const s = selectedSubRows.get(i);
			if (s && s.size > 0) return true;
		}
		return false;
	};

	// toggle functions
	const toggleSelectAll = (checked: boolean) => {
		if (!props.rows) return;
		if (checked) {
			const newSelectedRows = new Set<number>();
			const newSelectedSubRows = new Map<number, Set<number>>();
			props.rows.forEach((r, rowIndex) => {
				const sub = getSubItemsOfRow(r) ?? [];
				if (!sub || sub.length === 0) {
					newSelectedRows.add(rowIndex);
				} else {
					newSelectedSubRows.set(rowIndex, new Set(sub.map((_, si) => si)));
				}
			});
			setSelectedRows(newSelectedRows);
			setSelectedSubRows(newSelectedSubRows);
		} else {
			setSelectedRows(new Set());
			setSelectedSubRows(new Map());
		}
	};

	const toggleSelectRow = (rowIndex: number, checked: boolean) => {
		const sub = getSubItemsOfRow(props.rows![rowIndex]) ?? [];
		const newSelectedRows = new Set(selectedRows);
		const newSelectedSubRows = new Map(selectedSubRows);

		if (!sub || sub.length === 0) {
			// Row sin subrows
			if (checked) {
				newSelectedRows.add(rowIndex);
			} else {
				newSelectedRows.delete(rowIndex);
			}
		} else {
			// Row con subrows
			if (checked) {
				// Seleccionar todas las subrows y marcar la row como seleccionada
				newSelectedSubRows.set(rowIndex, new Set(sub.map((_, si) => si)));
				newSelectedRows.add(rowIndex);
			} else {
				newSelectedSubRows.delete(rowIndex);
				newSelectedRows.delete(rowIndex);
			}
		}
		setSelectedRows(newSelectedRows);
		setSelectedSubRows(newSelectedSubRows);
	};

	const toggleSelectSubRow = (
		rowIndex: number,
		subIndex: number,
		checked: boolean
	) => {
		const sub = getSubItemsOfRow(props.rows![rowIndex]) ?? [];
		const newSelectedRows = new Set(selectedRows);
		const newSelectedSubRows = new Map(selectedSubRows);
		const currentSet = newSelectedSubRows.get(rowIndex) ?? new Set<number>();

		if (checked) {
			currentSet.add(subIndex);
		} else {
			currentSet.delete(subIndex);
		}

		// Actualizar selectedSubRows
		if (currentSet.size === 0) {
			newSelectedSubRows.delete(rowIndex);
		} else {
			newSelectedSubRows.set(rowIndex, new Set(currentSet));
		}

		// Actualizar selectedRows basado en si todas las subrows están seleccionadas
		if (currentSet.size === sub.length) {
			// Todas las subrows están seleccionadas -> marcar la row como seleccionada
			newSelectedRows.add(rowIndex);
		} else {
			// No todas las subrows están seleccionadas -> desmarcar la row
			newSelectedRows.delete(rowIndex);
		}

		setSelectedRows(newSelectedRows);
		setSelectedSubRows(newSelectedSubRows);
	};

	useEffect(() => {
		if (props.defaultExpandAll && props.rows) {
			setExpandedRows(new Set(props.rows.map((_, i) => i)));
		}
	}, [props.rows?.length, props.defaultExpandAll]);

	useEffect(() => {
		if (props.onSelectionChange && hasCheckboxes) {
			const selectedRowsData = getSelectedRowData();

			const selectedSubRowsData = getSelectSubRowData();

			props?.onSelectionChange?.({
				rows: selectedRowsData,
				subRows: selectedSubRowsData,
				allSelected: areAllSelected(),
			});
		}
	}, [selectedRows, selectedSubRows]);

	useEffect(() => {
		if (props.onSelectionChange && hasCheckboxes) {
			const allCurrentlySelected = areAllSelected();
			const selectedRowsData = getSelectedRowData();
			const selectedSubRowsData = getSelectSubRowData();

			// Solo notificar si allSelected cambió debido a nuevos rows
			props?.onSelectionChange?.({
				rows: selectedRowsData,
				subRows: selectedSubRowsData,
				allSelected: allCurrentlySelected,
			});
		}
		// Este efecto solo se ejecuta cuando la longitud de props.rows cambia
		// lo que indica que se cargaron nuevos registros
	}, [props.rows?.length]);

	// Set columns to display
	useEffect(() => {
		// If row actions is not hidden append a fixed column
		if (!hideRowActions) {
			// Create the last column Action
			const actionsColumn = {
				header: props.hideRowActionHeader ? '' : 'Actions',
				content: (instance: T) => {
					if (props.rowActions) {
						// Get the row actions
						const actions = props.rowActions(instance);
						const filteredActions =
							filterRowOptionsByRolesOrPermissions(actions);
						if (filteredActions.length > 0) {
							return (
								<div onClick={(e) => e.stopPropagation()}>
									<Dropdown
										buttonContent={<EllipsisVerticalIcon className="w-6 h-6" />}
										showChevronDownIcon={false}
										disableHoverIndexes={
											props.disableHoverIndexesRowActionItems &&
											props.disableHoverIndexesRowActionItems(instance)
										}
										classNameMenuItems={
											props.classNameRowActionItems &&
											props.classNameRowActionItems(instance)
										}
										items={filteredActions.map((action) => ({
											...action,
											...instance,
										}))}
									/>
								</div>
							);
						} else {
							return null;
						}
					} else {
						return null;
					}
				},
			};

			// Append the actions column to the table
			const updatedColumns = props.columns.concat([actionsColumn]);
			// Save the columns to display
			setColumnsData(updatedColumns);
		} else {
			setColumnsData(props.columns);
		}

		if (props.subColumns) {
			setSubColumnsData(props.subColumns);
		}
		// test dependencies
	}, [
		hideRowActions,
		props.columns,
		props.subColumns,
		props.hideRowActionHeader,
		props.rowActions,
	]);

	const getSelectedRowData = () => {
		return (
			props.rows?.filter((_, index) => {
				// Si la row está directamente en selectedRows
				if (selectedRows.has(index)) {
					return true;
				}

				// Si la row tiene subrows y todas están seleccionadas
				const subItems = getSubItemsOfRow(props.rows![index]);
				if (subItems && subItems.length > 0) {
					const subSet = selectedSubRows.get(index);
					return subSet && subSet.size === subItems.length;
				}

				return false;
			}) || []
		);
	};

	const getSelectSubRowData = () => {
		const selectedSubRowsData: U[] = [];

		// Recorrer el Map de selectedSubRows correctamente
		selectedSubRows.forEach((subIndexSet, rowIndex) => {
			const row = props.rows?.[rowIndex];
			if (row && props.subRowsAccessor) {
				const subRows = props.subRowsAccessor(row);
				subIndexSet.forEach((subIndex) => {
					// Asegurarnos de que el subIndex esté dentro del rango de subRows
					if (subRows && subRows[subIndex]) {
						selectedSubRowsData.push(subRows[subIndex]);
					}
				});
			}
		});

		return selectedSubRowsData;
	};

	const toggleRow = (e: MouseEvent<HTMLButtonElement>, index: number) => {
		e.stopPropagation();
		setExpandedRows((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(index)) {
				newSet.delete(index);
			} else {
				newSet.add(index);
			}
			return newSet;
		});
	};

	return (
		<>
			<div
				className={`relative overflow-x-auto pr-4 scroll-custom shadow-md sm:rounded-lg border border-dark-10 ${props.classNameContainer}`}
			>
				{/* Table Container */}
				<table
					data-cy={props.dataCY}
					className="w-full text-sm text-left text-gray-500 dark:text-gray-400"
				>
					{/* Header columns */}
					<thead
						className={clsx(
							'text-xs sticky top-0 opacity-60 capitalize bg-gray-50 z-20',
							props.classNameHead
						)}
					>
						<tr className={`${props.classNameHeader}`}>
							{columnsData.map((column, index) => {
								return (
									<th
										key={`table-header-${index}`}
										scope="col"
										className={clsx(
											'px-4 py-3 font-normal text-opacity-black',
											props.classNameHeaderItem,
											{
												'place-self-end flex justify-end':
													props.headersAlignEnd?.includes(index),
											}
										)}
									>
										{/*
											If this is the very first column and hasCheckboxes is true,
											put there the global checkbox that selects all.
										*/}
										{index === 0 && hasCheckboxes ? (
											<div className="flex items-center gap-2">
												<Checkbox
													disabled={isCheckboxDisabled}
													checked={areAllSelected()}
													indeterminate={!areAllSelected() && isSomeSelected()}
													onChange={(e) => {
														toggleSelectAll(e.target.checked);
													}}
												/>
												{column.leftIcon && <>{column.leftIcon}</>}
												{column.header}
												{column.rightIcon && <>{column.rightIcon}</>}
											</div>
										) : (
											<>
												{column.leftIcon && <>{column.leftIcon}</>}
												{column.header}
												{column.rightIcon && <>{column.rightIcon}</>}
											</>
										)}
									</th>
								);
							})}
						</tr>
					</thead>
					{props.rows &&
						(props.rows.length > 0 ? (
							<tbody>
								{props.rows.map((row, rowIndex) => {
									const isPrevRowHovered = hoveredRowIndex === rowIndex - 1;
									const isHover = hoveredRowIndex === rowIndex;
									const isLastRow = rowIndex === (props.rows?.length || 0) - 1;
									return (
										<React.Fragment key={`table-row-fragment-${rowIndex}`}>
											<tr
												onMouseEnter={() => setHoveredRowIndex(rowIndex)}
												onMouseLeave={() => setHoveredRowIndex(null)}
												onClick={() => {
													if (props.onClickRow) {
														props.onClickRow(row, rowIndex);
													}
												}}
												onDoubleClick={() => {
													if (props.onDoubleClickRow) {
														props.onDoubleClickRow(row, rowIndex);
													}
												}}
												className={clsx(
													'bg-white border border-transparent relative',
													'dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-600',
													'hover:bg-grey-5 hover:rounded-[16px] hover:border-green-base active:bg-green-secondary',
													props.classNameRows,
													{
														'border-transparent':
															(rowIndex === 0 && props.hideFirstRowBorder) ||
															props.useAbsoluteBorder,
													},
													{
														'border-b':
															props.rows &&
															rowIndex === props?.rows.length - 1 &&
															props.showLastRowBorderBottom,
														'!border-t-dark-10 hover:!border-t-green-base':
															!isPrevRowHovered &&
															(!props.hideFirstRowBorder || rowIndex !== 0),
													},
													{
														'!bg-green-secondary rounded-2xl': row?.isActive,
													},
													{
														'pl-3.5': hasCheckboxes,
													}
												)}
												ref={isLastRow ? props.lastItemRef : null}
											>
												{columnsData.map((column, index) => {
													return (
														<td
															key={`table-row-item-${column.header}-${index}`}
															className={clsx(
																'px-4 py-3 flex items-center gap-2 min-w-0',
																{
																	'flex justify-end text-end':
																		props.columnsAlignEnd?.includes(index),
																	'!px-0':
																		subColumnsData.length > 0 &&
																		hasCheckboxes &&
																		index < columnsData.length - 1,
																},
																props.classNameColumn
															)}
														>
															{/* this div was made so it to simulate a border smaller than the div  and works with the useAbsoluteBorder prop*/}
															<div
																className={clsx(
																	'absolute top-0 left-4 right-4 h-[0.5px] bg-dark-10',
																	(rowIndex === 0 &&
																		props.hideFirstRowBorder) ||
																		!props.useAbsoluteBorder ||
																		isPrevRowHovered ||
																		isHover
																		? 'hidden'
																		: 'block'
																)}
															/>

															{/* If this is the first column and hasCheckboxes -> show row checkbox */}
															{index === 0 ? (
																<>
																	{/* stopPropagation to avoid row click/select issues */}
																	{(hasCheckboxes ||
																		subColumnsData.length > 0) && (
																		<div
																			className="flex"
																			onClick={(e) => e.stopPropagation()}
																		>
																			{/* if row has subrows -> checkbox controls subrows */}
																			{hasCheckboxes && (
																				<Checkbox
																					disabled={isCheckboxDisabled}
																					checked={isRowFullySelected(rowIndex)}
																					indeterminate={isRowPartiallySelected(
																						rowIndex
																					)}
																					onChange={(e) =>
																						toggleSelectRow(
																							rowIndex,
																							e.target.checked
																						)
																					}
																				/>
																			)}
																			{subColumnsData.length > 0 &&
																				index === 0 && (
																					<button
																						onClick={(e) =>
																							toggleRow(e, rowIndex)
																						}
																						className="ml-3 mr-3"
																					>
																						<Icon
																							src={Icons.simpleArrowRight}
																							className={clsx(
																								'!w-4 !h-4 flex-shrink-0 transition-transform duration-200',
																								{
																									'rotate-90':
																										expandedRows.has(rowIndex),
																									'text-black':
																										!props.hasRowError ||
																										!props.hasRowError(
																											row,
																											rowIndex
																										),
																									'text-main-red':
																										props.hasRowError &&
																										props.hasRowError(
																											row,
																											rowIndex
																										),
																								}
																							)}
																						/>
																					</button>
																				)}
																		</div>
																	)}
																	<div className="truncate min-w-0 flex-1">
																		{column.content(row, rowIndex)}
																	</div>
																</>
															) : (
																<div className="truncate min-w-0 flex-1">
																	{column.content(row, rowIndex)}
																</div>
															)}
														</td>
													);
												})}
											</tr>

											{/* to show one additional row */}
											{props.renderRowExtraContent &&
												props.renderRowExtraContent(row, rowIndex)}

											{expandedRows.has(rowIndex) && (
												<tr>
													<td
														colSpan={columnsData.length}
														className="border-transparent"
													>
														{subColumnsData.length > 0 ? (
															(() => {
																// Extract the array of sub-rows using the function or prop
																const subItems = props.subRowsAccessor
																	? props.subRowsAccessor(row)
																	: [];

																return subItems && subItems.length > 0 ? (
																	<table className="w-full border-t">
																		{/* If you want to omit the thead, simply don't include it */}
																		<tbody>
																			{subItems.map((subItem, subIndex) => (
																				<tr
																					key={subIndex}
																					className={clsx(
																						'cursor-pointer',
																						props.classNameSubRows
																					)}
																					onClick={() => {
																						props?.onClickSubRow?.(
																							subItem,
																							subIndex
																						);
																					}}
																				>
																					{subColumnsData.map(
																						(subCol, colIndex) => (
																							<td
																								key={colIndex}
																								className={clsx(
																									'px-4 py-2',
																									{
																										'!pl-[3.25rem]':
																											hasCheckboxes &&
																											colIndex === 0,
																									},
																									props.classNameSubColumn
																								)}
																								// onClick={(e) =>
																								// 	e.stopPropagation()
																								// }
																							>
																								{/* If this is the first sub-column and hasCheckboxes -> show subrow checkbox */}
																								{colIndex === 0 &&
																								hasCheckboxes ? (
																									<div className="flex items-center gap-2">
																										<Checkbox
																											disabled={
																												isCheckboxDisabled
																											}
																											checked={
																												!!(
																													selectedSubRows.get(
																														rowIndex
																													) &&
																													selectedSubRows
																														.get(rowIndex)!
																														.has(subIndex)
																												)
																											}
																											indeterminate={false}
																											onChange={(e) =>
																												toggleSelectSubRow(
																													rowIndex,
																													subIndex,
																													e.target.checked
																												)
																											}
																										/>
																										{subCol.content(
																											subItem,
																											subIndex
																										)}
																									</div>
																								) : (
																									subCol.content(
																										subItem,
																										subIndex
																									)
																								)}
																							</td>
																						)
																					)}
																				</tr>
																			))}
																		</tbody>
																	</table>
																) : (
																	<div className="p-4 text-sm italic">
																		{props.subRowEmptyMessage ??
																			'No sub-rows available'}
																	</div>
																);
															})()
														) : (
															<div>No sub-columns available</div>
														)}
													</td>
												</tr>
											)}
										</React.Fragment>
									);
								})}
							</tbody>
						) : !props.isLoading && props.tableEmptyComponent ? (
							<tbody>
								<tr>
									<td colSpan={columnsData.length}>
										{props.tableEmptyComponent}
									</td>
								</tr>
							</tbody>
						) : null)}
				</table>
			</div>
			{(!props.rows || props.isLoading) && (
				<LoaderScreen className={clsx('my-20', props.classNameLoader)} />
			)}
			{props.rows && hasPagination && props.rows.length > 0 ? (
				<PaginationCustom
					totalPages={props.totalPages ?? 0}
					className={clsx('hidden md:flex mt-8', props.classNamePagination)}
				/>
			) : null}
		</>
	);
};

export default SimpleTable;
